import type { components } from '@proposify/variables-bff-api-client';
import { z } from 'zod';
import { variableValidationErrorSchema, type VariableValidationError } from './VariablesEntity';

// Generated from openapi.yaml — compile-time drift check only (see below).
type GeneratedVariableNode = components['schemas']['VariableNode'];

export type CrmV2VariableShape = {
  type: string;
  format?: string;
  grouped?: boolean;
  currencyCode?: string;
  currencySymbol?: string;
};

export type CrmV2VariableNode = {
  name: string;
  friendlyName: string;
  path: string;
  // No v1 legacy producer here (unlike VariablesEntity.ts's shared type), so
  // this stays string | null with no number fallback.
  value: string | null;
  type?: string;
  visible?: boolean;
  template?: string | null;
  objectId?: string;
  shape: CrmV2VariableShape;
  validationErrors?: VariableValidationError[];
  variables: CrmV2VariableNode[] | null;
};

type AssertAssignable<_A extends B, B> = true;

// Per-field drift check against the generated contract, same rationale as
// VariablesEntity.ts's own check. `shape`/`variables` excluded for the same
// reasons documented there.
export type _CheckName = AssertAssignable<CrmV2VariableNode['name'], GeneratedVariableNode['name']>;
export type _CheckFriendlyName = AssertAssignable<CrmV2VariableNode['friendlyName'], GeneratedVariableNode['friendlyName']>;
export type _CheckPath = AssertAssignable<CrmV2VariableNode['path'], GeneratedVariableNode['path']>;
export type _CheckValue = AssertAssignable<CrmV2VariableNode['value'], GeneratedVariableNode['value']>;
export type _CheckType = AssertAssignable<NonNullable<CrmV2VariableNode['type']>, GeneratedVariableNode['type']>;
export type _CheckVisible = AssertAssignable<NonNullable<CrmV2VariableNode['visible']>, GeneratedVariableNode['visible']>;
export type _CheckTemplate = AssertAssignable<NonNullable<CrmV2VariableNode['template']>, GeneratedVariableNode['template']>;
export type _CheckObjectId = AssertAssignable<NonNullable<CrmV2VariableNode['objectId']>, GeneratedVariableNode['objectId']>;

export const CRM_V2_VALIDATION_CODES = {
  NUMERIC_FORMAT_MISSING: 'NUMERIC_FORMAT_MISSING',
  FORMAT_IS_NOT_A_TYPE: 'FORMAT_IS_NOT_A_TYPE',
  ARRAY_GROUPED_MISSING: 'ARRAY_GROUPED_MISSING',
  GROUPED_ON_NON_ARRAY: 'GROUPED_ON_NON_ARRAY',
  CURRENCY_METADATA_WITHOUT_FORMAT: 'CURRENCY_METADATA_WITHOUT_FORMAT',
  CONTAINER_VALUE_NOT_NULL: 'CONTAINER_VALUE_NOT_NULL',
  NOT_DECIMAL_LITERAL: 'NOT_DECIMAL_LITERAL',
  UNPARSEABLE: 'UNPARSEABLE',
} as const;

const STRUCTURAL_SHAPE_TYPES = ['string', 'number', 'integer', 'boolean', 'object', 'array'] as const;
const NUMERIC_SHAPE_TYPES = ['number', 'integer'] as const;
const CONTAINER_SHAPE_TYPES = ['object', 'array'] as const;

// RFC 102 clause 11: a decimal literal is digits with an optional fractional part —
// exponent notation ('1e+21') would lose precision in a JS consumer and is rejected.
const DECIMAL_LITERAL_PATTERN = /^-?\d+(\.\d+)?$/;

const crmV2VariableShapeSchema = z
  .object({
    type: z.string().min(1),
    format: z.string().optional(),
    grouped: z.boolean().optional(),
    currencyCode: z.string().optional(),
    currencySymbol: z.string().optional(),
  })
  .strict();

// Recursive structural schema only — no business-rule validation here (D1). Descendants
// are checked for shape only; cross-field rules apply once, at the top-level node.
const crmV2VariableNodeStructureSchema: z.ZodType<CrmV2VariableNode> = z.lazy(() =>
  z
    .object({
      name: z.string(),
      friendlyName: z.string(),
      path: z.string(),
      value: z.string().nullable(),
      type: z.string().optional(),
      visible: z.boolean().optional(),
      template: z.string().nullable().optional(),
      objectId: z.string().optional(),
      shape: crmV2VariableShapeSchema,
      validationErrors: z.array(variableValidationErrorSchema).optional(),
      variables: z.array(crmV2VariableNodeStructureSchema).nullable(),
    })
    .strict()
);

// D1: depth-0 only. A recursive rule set would reject `flagged-node-in-otherwise-valid-tree`
// (grandchild violates NUMERIC_FORMAT_MISSING) even though it's an expectSchemaValid: true
// golden vector — the producer only structurally checks the top-level node.
function applyCrmV2ContractRules(node: CrmV2VariableNode, ctx: z.RefinementCtx): void {
  const { shape, value } = node;

  if (!(STRUCTURAL_SHAPE_TYPES as readonly string[]).includes(shape.type)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${CRM_V2_VALIDATION_CODES.FORMAT_IS_NOT_A_TYPE}: shape.type "${shape.type}" is a format value, not a structural type`,
    });
  }

  const isNumeric = (NUMERIC_SHAPE_TYPES as readonly string[]).includes(shape.type);
  const isContainer = (CONTAINER_SHAPE_TYPES as readonly string[]).includes(shape.type);

  if (isNumeric && !shape.format) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${CRM_V2_VALIDATION_CODES.NUMERIC_FORMAT_MISSING}: numeric shape has no format`,
    });
  }

  if (shape.type === 'array' && shape.grouped === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${CRM_V2_VALIDATION_CODES.ARRAY_GROUPED_MISSING}: array shape has no grouped flag`,
    });
  }

  if (shape.grouped !== undefined && shape.type !== 'array') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${CRM_V2_VALIDATION_CODES.GROUPED_ON_NON_ARRAY}: grouped is set on a non-array shape`,
    });
  }

  if ((shape.currencyCode !== undefined || shape.currencySymbol !== undefined) && shape.format !== 'currency') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${CRM_V2_VALIDATION_CODES.CURRENCY_METADATA_WITHOUT_FORMAT}: currency metadata present without format currency`,
    });
  }

  if (isContainer && value !== null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${CRM_V2_VALIDATION_CODES.CONTAINER_VALUE_NOT_NULL}: container shape carries a non-null value`,
    });
  }

  // Money rule: numeric shapes must carry a decimal-literal string. A raw JSON number
  // (e.g. 1e21, 9007199254740993) is now rejected earlier, by the base schema itself
  // (`value: z.string().nullable()`) — value is guaranteed to be string | null here.
  if (isNumeric && value !== null && !DECIMAL_LITERAL_PATTERN.test(value)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${CRM_V2_VALIDATION_CODES.NOT_DECIMAL_LITERAL}: value ${value} is not a decimal-literal string`,
    });
  }
}

export const crmV2VariableNodeSchema = crmV2VariableNodeStructureSchema.superRefine(applyCrmV2ContractRules);
